import { isNil } from 'lodash/fp';
import { type Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { JourneyStage } from '../StandardApplicationEntrypoint/Journey/JourneyController';
import { type Action, type State } from '../StandardApplicationEntrypoint/Journey/reducer';
import { type EventApplicationState } from './Journey/JourneyController';

const useEventLayout = (
    dispatch: Dispatch<Action<EventApplicationState>>,
    state: State<EventApplicationState>
): {
    title: string;
    onBack: () => void;
    shouldShowBackIcon?: boolean;
    /**
     * This is used to determine whether to wrap the content page with JourneyLayout. default is true.
     */
    useJourneyLayout?: boolean;
} => {
    const { t } = useTranslation([
        'customerDetails',
        'journey',
        'paymentDetails',
        'confirmRemoteFlow',
        'otpPage',
        'appointmentPage',
    ]);
    const navigate = useNavigate();

    const { application, stage: journeyStage } = state;
    const { draftFlow } = application;

    const shouldShowBackIcon = !application.withCustomerDevice;
    const isApplyingFromDetails =
        !isNil(draftFlow.isApplyingForFinanceCompleted) || !isNil(draftFlow.isApplyingForInsuranceCompleted);

    const useJourneyLayout = true;

    switch (journeyStage) {
        case JourneyStage.ApplicantKYC:
            return {
                title: t('customerDetails:title'),
                onBack: shouldShowBackIcon ? () => navigate(-1) : () => {},
                shouldShowBackIcon: shouldShowBackIcon && !isApplyingFromDetails,
                useJourneyLayout: false,
            };

        case JourneyStage.GuarantorKYC:
            return {
                title: t('customerDetails:title'),
                onBack: shouldShowBackIcon ? () => navigate(-1) : () => {},
                shouldShowBackIcon,
                useJourneyLayout,
            };

        case JourneyStage.Deposit:
            return {
                title: t('paymentDetails:title'),
                onBack: () => dispatch({ type: 'goTo', stage: JourneyStage.ApplicantKYC }),
                shouldShowBackIcon,
                useJourneyLayout,
            };

        case JourneyStage.Appointment:
            return {
                title: t('appointmentPage:title'),
                onBack: () => navigate('..'),
                shouldShowBackIcon,
                useJourneyLayout,
            };

        case JourneyStage.ConfirmEmailSend:
            return {
                title: t('confirmRemoteFlow:title'),
                onBack: () => {},
                shouldShowBackIcon,
                useJourneyLayout: false,
            };

        case JourneyStage.Otp:
            return {
                title: t('otpPage:title'),
                onBack: () => navigate('..'),
                shouldShowBackIcon,
                useJourneyLayout,
            };

        default:
            return {
                title: '',
                onBack: () => {},
                shouldShowBackIcon,
                useJourneyLayout,
            };
    }
};

export default useEventLayout;
